import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { format, differenceInDays, isValid } from "date-fns";
import {
  Calendar,
  Clock,
  FileText,
  Info,
  CheckCircle,
  XCircle,
  AlertCircle,
  IndianRupee,
  CreditCard
} from "lucide-react";
import { Compensation } from "@/types/models";

interface ViewCompensationDialogProps {
  compensation: Compensation;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ViewCompensationDialog({
  compensation,
  open,
  onOpenChange,
}: ViewCompensationDialogProps) {
  // Helper function to check if compensation needs notification
  const needsNotification = (compensation: Compensation) => {
    if (!compensation.endDate || !compensation.notifyPeriodInDays) {
      return false;
    }

    const today = new Date();
    const endDate = new Date(compensation.endDate);
    const notifyDays = parseInt(compensation.notifyPeriodInDays, 10);

    if (isNaN(notifyDays)) {
      return false;
    }

    // Calculate notification date (end date - notify days)
    const notificationDate = new Date(endDate);
    notificationDate.setDate(notificationDate.getDate() - notifyDays);

    // Check if today is within the notification period
    return today >= notificationDate && today <= endDate;
  };

  // Get compensation status
  const getCompensationStatus = (compensation: Compensation) => {
    const today = new Date();
    const startDate = compensation.startDate ? new Date(compensation.startDate) : null;
    const endDate = compensation.endDate ? new Date(compensation.endDate) : null;

    if (!startDate) {
      return {
        status: 'draft',
        label: 'Draft',
        color: 'bg-gray-50 text-gray-700 border-gray-200',
        icon: <Clock className="h-4 w-4" />
      };
    }

    if (today < startDate) {
      return {
        status: 'upcoming',
        label: 'Upcoming',
        color: 'bg-blue-50 text-blue-700 border-blue-200',
        icon: <Clock className="h-4 w-4" />
      };
    }

    if (endDate && today > endDate) {
      return {
        status: 'expired',
        label: 'Expired',
        color: 'bg-red-50 text-red-700 border-red-200',
        icon: <XCircle className="h-4 w-4" />
      };
    }

    // Check if compensation needs notification
    if (needsNotification(compensation)) {
      return {
        status: 'notification',
        label: 'Expiring Soon',
        color: 'bg-orange-50 text-orange-700 border-orange-200',
        icon: <AlertCircle className="h-4 w-4" />
      };
    }

    return {
      status: 'active',
      label: 'Active',
      color: 'bg-green-50 text-green-700 border-green-200',
      icon: <CheckCircle className="h-4 w-4" />
    };
  };

  const statusInfo = getCompensationStatus(compensation);
  const daysUntilExpiry = differenceInDays(new Date(compensation.endDate), new Date());
  const daysUntilStart = differenceInDays(new Date(compensation.startDate), new Date());

  const formatDate = (date: any) => {
    if (date && isValid(new Date(date))) {
      return format(new Date(date), "PPP");
    }
    return "Not specified";
  };

  const formatCurrency = (amount: any) => {
    if (amount && !isNaN(amount)) {
      return `₹${Number(amount).toLocaleString("en-IN")}`;
    }
    return "Not specified";
  };

  const renderInfoItem = (
    icon: React.ReactNode,
    label: string,
    value: string,
    iconColor: string = "text-blue-600"
  ) => (
    <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0`}>
          <div className={iconColor}>{icon}</div>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{label}</p>
          <p className="font-semibold text-gray-900 text-sm leading-tight">
            {value === "Not provided" || value === "Not specified" ? (
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                {value}
              </span>
            ) : (
              value
            )}
          </p>
        </div>
      </div>
    </div>
  );

  const renderSectionHeader = (
    icon: React.ReactNode,
    title: string,
    _bgColor: string,
    iconBgColor: string,
    iconColor: string
  ) => (
    <div className={`flex items-center gap-3 mb-6`}>
      <div className={`w-10 h-10 ${iconBgColor} rounded-xl flex items-center justify-center`}>
        <div className={iconColor}>{icon}</div>
      </div>
      <h2 className="text-xl font-bold text-gray-900">{title}</h2>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[1000px] h-[95vh] flex flex-col p-0 rounded-xl overflow-hidden">
        <DialogHeader className="px-4 pb-0">
          <DialogTitle className="sr-only">
            Compensation Details - {compensation.employee.user.firstName} {compensation.employee.user.lastName}
          </DialogTitle>
          <DialogDescription className="sr-only">
            View complete compensation information including employee details, salary information, and status.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-4">
            {/* Employee Header */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100 shadow-sm">
              <div className="flex items-center gap-6">
                <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                  {compensation.employee.user.firstName[0]}
                  {compensation.employee.user.lastName[0]}
                </div>

                <div className="flex-1">
                  <div className="flex items-center gap-2 justify-between">
                    <h1 className="text-3xl font-bold text-gray-900 mb-1">
                      {compensation.employee.user.firstName} {compensation.employee.user.lastName}
                    </h1>
                    <span className="flex items-end gap-2">
                      <Badge
                        variant="outline"
                        className={`text-xs ${statusInfo.color}`}
                      >
                        <span className="flex items-center gap-1">
                          {statusInfo.icon}
                          {statusInfo.label}
                        </span>
                      </Badge>
                    </span>
                  </div>


                  <div>
                    <div className="text-sm text-gray-500 mb-2">
                      {compensation.employee.user.email}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-600">

                    <span className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      {daysUntilStart > 0 ? (
                        <span className="text-blue-600 font-medium">
                          Starts in {daysUntilStart} days
                        </span>
                      ) : daysUntilExpiry < 0 ? (
                        <span className="text-red-600 font-medium">
                          Expired {Math.abs(daysUntilExpiry)} days ago
                        </span>
                      ) : (
                        <span>
                          {daysUntilExpiry} days remaining
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Compensation Details */}
            <div className="bg-purple-50 rounded-xl p-6 shadow-sm border border-purple-100">
              {renderSectionHeader(
                <FileText className="h-5 w-5" />,
                "Compensation Details",
                "bg-purple-50",
                "bg-purple-100",
                "text-purple-600"
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {renderInfoItem(
                  <Calendar className="h-4 w-4" />,
                  "Start Date",
                  formatDate(compensation.startDate),
                  "text-purple-600"
                )}
                {renderInfoItem(
                  <Calendar className="h-4 w-4" />,
                  "End Date",
                  formatDate(compensation.endDate),
                  "text-purple-600"
                )}
                {renderInfoItem(
                  <IndianRupee className="h-4 w-4" />,
                  "New Salary",
                  formatCurrency(compensation.newSalary),
                  "text-purple-600"
                )}
                {renderInfoItem(
                  <Clock className="h-4 w-4" />,
                  "Pay Frequency",
                  compensation.payFrequency || "Not provided",
                  "text-purple-600"
                )}
                {renderInfoItem(
                  <Clock className="h-4 w-4" />,
                  "Notice Period",
                  `${compensation.noticePeriodInDays} days`,
                  "text-purple-600"
                )}
                {renderInfoItem(
                  <Clock className="h-4 w-4" />,
                  "Notify Period",
                  `${compensation.notifyPeriodInDays} days`,
                  "text-purple-600"
                )}
                {renderInfoItem(
                  <CreditCard className="h-4 w-4" />,
                  "Salary Mode",
                  compensation.salaryMode || "Not provided",
                  "text-purple-600"
                )}

                {compensation.note && (
                  <div className="col-span-full">
                    {renderInfoItem(
                      <Info className="h-4 w-4" />,
                      "Notes",
                      compensation.note,
                      "text-purple-600"
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

